/* lib/theme.js — ONE appearance setting for the whole platform (V5 settings
 * framework, 2026-08-05). The audit found the theme implemented 5× across 3
 * localStorage keys; this is now THE engine every surface consumes:
 *
 *   · canonical key `lhc_theme` — the index.html pre-paint script reads it
 *     before any stylesheet arrives (dark must never flash white);
 *   · the Catalog Technician's old `lhc-os-theme` key is ADOPTED on first
 *     read and MIRRORED on every write, so any stale reader keeps working;
 *   · setTheme() also stamps the store/studio DOM contract (`data-theme` on
 *     <html> + <body>) and broadcasts a change event, so every mounted app
 *     (and other tabs, via the storage event) follows one choice;
 *   · no stored choice → the OS preference decides.
 *
 * Fixed-look shells (Sales CRM = dark, SuperAdmin = light) are deliberate
 * design decisions and do not consume this engine. */

const KEY = "lhc_theme";
const LEGACY_OS = "lhc-os-theme";   // pre-unification Catalog Technician key
const EVT = "lhc-theme-change";

function normalize(t) { return t === "dark" ? "dark" : "light"; }

export function getTheme() {
  try {
    const t = localStorage.getItem(KEY);
    if (t === "dark" || t === "light") return t;
    // adopt the technician's old key so a dark-OS catalog user keeps dark
    const os = localStorage.getItem(LEGACY_OS);
    if (os === "dark" || os === "light") {
      try { localStorage.setItem(KEY, os); } catch (e) { /* private mode */ }
      return os;
    }
    return (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light";
  } catch (e) { return "light"; }
}

export function setTheme(t) {
  const v = normalize(t);
  try { localStorage.setItem(KEY, v); } catch (e) { /* private mode */ }
  try { localStorage.setItem(LEGACY_OS, v); } catch (e) { /* private mode */ }
  // the store/studio DOM contract (prototype: :root tokens + body overrides)
  try {
    document.documentElement.setAttribute("data-theme", v);
    document.body.setAttribute("data-theme", v);
  } catch (e) { /* no DOM */ }
  try { window.dispatchEvent(new CustomEvent(EVT, { detail: v })); } catch (e) { /* no DOM */ }
  return v;
}

/** subscribeTheme(fn) → unsubscribe. Fires on same-tab setTheme() and on
 *  other-tab changes (storage event). fn receives "light" | "dark". */
export function subscribeTheme(fn) {
  const onEvt = (e) => fn(normalize(e && e.detail));
  const onStorage = (e) => {
    if (e && e.key === KEY && (e.newValue === "dark" || e.newValue === "light")) fn(e.newValue);
  };
  window.addEventListener(EVT, onEvt);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVT, onEvt);
    window.removeEventListener("storage", onStorage);
  };
}
